import { CalendarDate } from '@internationalized/date';
import {
  enumBookingSortOption,
  enumBookingStatus,
  BookingSortOption,
  BookingStatus,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { bookingBulkVoidAtom } from '../_graphql/mutationBookingBulkVoid';
import { bookingDeleteAtom } from '../_graphql/mutationBookingDelete';
import { bookingMarkAsVoidedAtom } from '../_graphql/mutationBookingMarkAsVoided';
import { bookingsListQueryAtom } from '../_graphql/queryBookingsList';
import { bookingStatusCountsQueryAtom } from '../_graphql/queryBookingStatusCounts';

import { BookingRecord } from '~/modules/trade/types/booking';
import { atomFromQueryString } from '~/shared/state';

interface AffectedLoad {
  id: string;
  documentNumber: string;
  status: LoadStatus;
}

export interface BookingVoidValidation {
  bookingId: string;
  canVoid: boolean;
  requiresForceUnlink: boolean;
  blockingLoads: AffectedLoad[];
  unlinkableLoads: AffectedLoad[];
  currentStatus?: BookingStatus;
}
type Booking = { id: string; displayNumber: string; status: BookingStatus };

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedStatus = atomFromQueryString.string<string>('s');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $dateRange = atomFromQueryString.dateRange();

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isDialogOpen = atom<boolean>(false);
export const $editBooking = atom<BookingRecord | null>(null);
export const $isLoadDialogOpen = atom<boolean>(false);
export const $selectedBooking = atom<BookingRecord | null>(null);
export const $isVoidDialogOpen = atom<boolean>(false);
export const $currentSorting = atom<BookingSortOption[]>([
  enumBookingSortOption.CreatedAtDesc,
]);

// Bulk void dialog state
export const $isBulkVoidDialogOpen = atom<boolean>(false);
export const $bulkVoidBookings = atom<Booking[]>([]);

// 🟢🟢🟢 COMPUTED DATA -> real statuses array by selected status filter
export const $statuses = atom((get) => {
  const selectedStatus = get($selectedStatus);

  if (selectedStatus === 'all' || !selectedStatus) return [];

  const statusFilterOptions: { key: string; value: BookingStatus[] }[] = [
    {
      key: 'all',
      value: [],
    },
    {
      key: 'draft',
      value: [enumBookingStatus.Draft],
    },
    {
      key: 'open',
      value: [enumBookingStatus.Open],
    },
    {
      key: 'shipped',
      value: [enumBookingStatus.Shipped],
    },
    {
      key: 'closed',
      value: [enumBookingStatus.Completed, enumBookingStatus.Voided],
    },
  ];

  return (
    statusFilterOptions.find((option) => option.key === selectedStatus)
      ?.value ?? []
  );
});

// 🟢🟢🟢 REMOTE DATA
export const $remoteBookings = bookingsListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedStatuses = get($statuses);
  const dateRange = get($dateRange);
  const limit = get($limit);
  const offset = get($offset);
  const sortingParams = get($currentSorting);

  const params: {
    searchTerm?: string;
    status?: BookingStatus[];
    fromCutOffDate?: CalendarDate;
    toCutOffDate?: CalendarDate;
    limit: number;
    offset: number;
    sorting: BookingSortOption[];
  } = {
    limit,
    offset,
    sorting: sortingParams,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (selectedStatuses.length > 0) params.status = selectedStatuses;
  if (dateRange?.start) {
    params.fromCutOffDate = dateRange.start as CalendarDate;
  }
  if (dateRange?.end) {
    params.toCutOffDate = dateRange.end as CalendarDate;
  }

  return {
    variables: params,
  };
});

// 🟢🟢🟢 REMOTE DATA -> status counts
export const $bookingsCountingByStatus = bookingStatusCountsQueryAtom();

// 🟠🟠🟠 ACTION -> DELETE BOOKING
export const $deleteBooking = bookingDeleteAtom({
  onSuccess: (get) => {
    // Refetch bookings after successful delete
    get($remoteBookings).refetch?.();
    get($bookingsCountingByStatus).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> VOID BOOKING
export const $voidBooking = bookingMarkAsVoidedAtom({
  onSuccess: (get) => {
    // Refetch bookings after successful void
    get($remoteBookings).refetch?.();
    get($bookingsCountingByStatus).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> BULK VOID BOOKING
export const $bulkVoidBooking = bookingBulkVoidAtom({
  onSuccess: (get) => {
    // Refetch bookings after successful void
    get($remoteBookings).refetch?.();
    get($bookingsCountingByStatus).refetch?.();
  },
});

// 🟢🟢🟢 HELPER -> Create fetch validation function for VoidEntityDialog
// This calls the bulk void mutation with forceUnlink=false to validate
export const createBookingsVoidValidationFetcher =
  (
    bulkVoidMutation: (params: {
      ids: string[];
      forceUnlink?: boolean;
    }) => Promise<{
      tradeBookingBulkVoid: {
        results: Array<{
          booking: { id: string; status: BookingStatus } | null;
          blockingLoads: Array<{
            id: string;
            documentNumber: string;
            status: LoadStatus;
          }> | null;
          unlinkableLoads: Array<{
            id: string;
            documentNumber: string;
            status: LoadStatus;
          }> | null;
          requiresConfirmation: boolean;
        }>;
        successCount: number;
        failedCount: number;
      };
    }>,
  ) =>
  async (bookingIds: string[]): Promise<Map<string, BookingVoidValidation>> => {
    const results = new Map<string, BookingVoidValidation>();

    if (bookingIds.length === 0) {
      return results;
    }

    const response = await bulkVoidMutation({
      ids: bookingIds,
      forceUnlink: false,
    });

    // Results are in same order as input IDs, use index to correlate
    response.tradeBookingBulkVoid.results.forEach((item, index) => {
      const bookingId = bookingIds[index];

      const blockingLoads: AffectedLoad[] = (item.blockingLoads ?? []).map(
        (load) => ({
          id: load.id,
          documentNumber: load.documentNumber,
          status: load.status,
        }),
      );

      const unlinkableLoads: AffectedLoad[] = (item.unlinkableLoads ?? []).map(
        (load) => ({
          id: load.id,
          documentNumber: load.documentNumber,
          status: load.status,
        }),
      );

      const hasBlockingLoads = blockingLoads.length > 0;
      const hasUnlinkableLoads = unlinkableLoads.length > 0;
      const currentStatus = item.booking?.status;
      const isAlreadyVoided = currentStatus === enumBookingStatus.Voided;

      results.set(bookingId, {
        bookingId,
        canVoid: !hasBlockingLoads && !isAlreadyVoided,
        requiresForceUnlink: hasUnlinkableLoads && !hasBlockingLoads,
        blockingLoads,
        unlinkableLoads,
        currentStatus,
      });
    });

    return results;
  };
